import { useUser } from "@hooks/useAuth";
import { useUIStore } from "@store/uiStore";
import { cn } from "@utils";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "remixicon/fonts/remixicon.css";

// Navigation items for students
const studentNavigation = [
  { name: "Dashboard", href: "/dashboard", icon: "ri-home-5-line" },
  { name: "Lessons", href: "/lessons", icon: "ri-book-open-line" },
  { name: "Readers", href: "/readers", icon: "ri-book-read-line" },
  { name: "Financial", href: "/financial", icon: "ri-wallet-3-line" },
  { name: "Profile", href: "/profile", icon: "ri-user-line" },
];

// Navigation items for teachers (Teacher Panel first, then shared items)
const teacherNavigation = [
  { name: "Teacher Panel", href: "/teacher", icon: "ri-presentation-line" },
  {
    name: "Attendance",
    href: "/teacher/attendance",
    icon: "ri-checkbox-circle-line",
  },
  { name: "Readers", href: "/readers", icon: "ri-book-read-line" },
  { name: "Profile", href: "/profile", icon: "ri-user-line" },
];

// Navigation items for director (includes Admin)
const directorNavigation = [
  { name: "Teacher Panel", href: "/teacher", icon: "ri-presentation-line" },
  {
    name: "Attendance",
    href: "/teacher/attendance",
    icon: "ri-checkbox-circle-line",
  },
  { name: "Admin", href: "/admin", icon: "ri-shield-user-line" },
  { name: "Readers", href: "/readers", icon: "ri-book-read-line" },
  { name: "Profile", href: "/profile", icon: "ri-user-line" },
];

// Check if user is a teacher or admin (staff)
function isTeacherOrAdmin(
  user: { role?: string; isStaff?: boolean } | null,
): boolean {
  if (!user) return false;
  // Teachers, admins, and staff users (like Elienai) can access teacher navigation
  if (user.role === "admin" || user.role === "teacher" || user.isStaff)
    return true;
  return false;
}

const isMobileViewport = () =>
  typeof window !== "undefined" && window.innerWidth < 1024;

export default function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const { data: user, isLoading } = useUser();

  const initials = user?.firstName?.[0] || "U";

  // While the mobile drawer is open, lock body scroll and close on Escape.
  useEffect(() => {
    if (!sidebarOpen || !isMobileViewport()) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [sidebarOpen, setSidebarOpen]);

  // Close the drawer after navigating on mobile.
  const handleNavClick = () => {
    if (isMobileViewport()) setSidebarOpen(false);
  };

  // Build navigation based on user role
  // Directors (isStaff) get admin navigation, teachers get teacher navigation
  // Only show navigation if user exists and matches expected role
  const navigation = user?.isStaff
    ? directorNavigation
    : isTeacherOrAdmin(user ?? null)
      ? teacherNavigation
      : studentNavigation;

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex flex-col bg-earth-700 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
          "transform lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-5 border-b border-earth-800">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <img
                src="/logo.crop.png"
                alt="Beyond Class"
                className="h-8 w-8 object-cover rounded-full"
              />
              <h1 className="text-lg font-semibold text-white tracking-tight">
                Beyond Class
              </h1>
            </div>
          ) : (
            <img
              src="/logo.crop.png"
              alt="BC"
              className="h-8 w-8 object-cover rounded-full"
            />
          )}
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex h-8 w-8 items-center justify-center rounded-lg"
            aria-label="Collapse sidebar"
          >
            <i
              className={cn(
                "text-earth-400 text-lg px-1 py-1 hover:text-white transition-colors",
                sidebarOpen ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line",
              )}
            ></i>
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="flex lg:hidden h-8 w-8 items-center justify-center rounded-lg text-earth-400 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-6">
          {navigation.map((item) => {
            const isActive = location.pathname.startsWith(item.href);

            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-earth-600 text-white"
                    : "text-earth-400 hover:bg-earth-600 hover:text-white",
                )}
              >
                <i
                  className={cn(
                    item.icon,
                    "text-xl flex-shrink-0",
                    isActive ? "text-white" : "text-earth-500",
                  )}
                ></i>
                {sidebarOpen && <span>{item.name}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-earth-800 p-4">
          <div
            className={cn(
              "flex items-center gap-3",
              !sidebarOpen && "justify-center",
            )}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sand-700 text-white font-semibold text-sm">
              {isLoading ? "..." : initials}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {isLoading ? "Loading..." : user?.fullName || "User"}
                </p>
                <p className="text-xs text-earth-400 truncate">
                  {isLoading ? "" : user?.email}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
